// City name overrides: map outdated/incorrect names to preferred names
const CITY_NAME_OVERRIDES: Record<string, string> = {
	bangalore: 'Bengaluru',
};

const normalizeCityName = (name: string): string => CITY_NAME_OVERRIDES[name.toLowerCase()] ?? name;

type CityIndex = ReadonlyArray<{ lower: string; original: string }>;

// Lazily-built sorted index. The cities-list package is ~1 MB gzipped, so we
// dynamic-import it on first use. Until the chunk is fetched, searchCities
// falls back to the curated POPULAR_CITIES list.
let citiesIndex: CityIndex | null = null;
let citiesPromise: Promise<CityIndex> | null = null;

async function loadCitiesIndex(): Promise<CityIndex> {
	const mod = await import('cities-list');
	const list = (mod.default ?? mod) as Record<string, unknown>;
	return Object.keys(list)
		.map((city) => {
			const normalized = normalizeCityName(city);
			return { lower: normalized.toLowerCase(), original: normalized };
		})
		.sort((a, b) => (a.lower < b.lower ? -1 : a.lower > b.lower ? 1 : 0));
}

/**
 * Kicks off the cities chunk download. Safe to call repeatedly — only fetches once.
 * Resolves with the index; subsequent searchCities calls become fully populated.
 */
export function ensureCitiesReady(): Promise<CityIndex> {
	if (!citiesPromise) {
		citiesPromise = loadCitiesIndex().then((idx) => {
			citiesIndex = idx;
			return idx;
		});
	}
	return citiesPromise;
}

// Curated list of popular/frequently used locations shown when no search query is typed
const POPULAR_CITIES = [
	'Remote',
	'New York',
	'San Francisco',
	'London',
	'Singapore',
	'Dubai',
	'Toronto',
	'Berlin',
	'Sydney',
	'Tokyo',
	'Bengaluru',
	'Mumbai',
	'Delhi',
	'Hyderabad',
	'Chennai',
	'Pune',
	'Kolkata',
	'Ahmedabad',
	'Gurugram',
	'Noida',
	'Los Angeles',
	'Austin',
	'Seattle',
	'Amsterdam',
	'Paris',
];

const CITIES_SEARCH_LIMIT = 50;

/**
 * Binary search: returns the index of the first entry whose `lower` value is
 * >= `prefix` (i.e. the leftmost position where prefix matches could begin).
 */
function findPrefixStart(index: CityIndex, prefix: string): number {
	let lo = 0;
	let hi = index.length;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if (index[mid].lower < prefix) lo = mid + 1;
		else hi = mid;
	}
	return lo;
}

/**
 * Search cities with tiered relevance ranking:
 *   Tier 1 – name starts with the query            ("ban" → "Bangalore")
 *   Tier 2 – a word inside the name starts with it ("york" → "New York")
 *   Tier 3 – name contains the query anywhere      ("alore" → "Bangalore")
 *
 * "Remote" is always checked first and prepended when it matches.
 * Falls back to POPULAR_CITIES when no query is provided OR while the
 * cities-list chunk is still loading.
 */
export function searchCities(query: string): string[] {
	const trimmed = query.trim().toLowerCase();
	if (!trimmed) return POPULAR_CITIES;

	// Kick off the lazy load; if not ready yet, return popular cities as a
	// graceful fallback. The hook re-runs once the chunk resolves.
	if (!citiesIndex) {
		ensureCitiesReady();
		return POPULAR_CITIES.filter((c) => c.toLowerCase().includes(trimmed));
	}

	const index = citiesIndex;
	const prefixMatches: string[] = [];
	const wordMatches: string[] = [];
	const containsMatches: string[] = [];

	// Tier 1: prefix matches via binary search
	const start = findPrefixStart(index, trimmed);
	for (let i = start; i < index.length; i++) {
		const { lower, original } = index[i];
		if (!lower.startsWith(trimmed)) break; // sorted, so first miss means done
		if (original !== 'Remote') prefixMatches.push(original);
		if (prefixMatches.length >= CITIES_SEARCH_LIMIT) break;
	}

	// Tiers 2 & 3: full scan (only if we still have room)
	if (prefixMatches.length < CITIES_SEARCH_LIMIT) {
		const prefixSet = new Set(prefixMatches);

		for (const { lower, original } of index) {
			if (wordMatches.length + containsMatches.length >= CITIES_SEARCH_LIMIT) break;
			if (original === 'Remote' || prefixSet.has(original)) continue;

			if (lower.split(' ').some((word) => word.startsWith(trimmed))) {
				wordMatches.push(original);
			} else if (lower.includes(trimmed)) {
				containsMatches.push(original);
			}
		}
	}

	// Prepend "Remote" when it matches
	const remoteFirst: string[] = 'remote'.startsWith(trimmed) ? ['Remote'] : [];

	return [...remoteFirst, ...prefixMatches, ...wordMatches, ...containsMatches].slice(
		0,
		CITIES_SEARCH_LIMIT
	);
}
